import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { buildClassName } from 'utils/build-class-name';
import cardStyles from './ui/card.module.scss';
import styles from './EducationalContent.module.scss';

const EducationalContent = () => {
  return (
    <div className={styles.component}>
      <div className={styles.cardsGrid}>
        <Card className={styles.card}>
          <CardHeader className={styles.header}>
            <CardTitle className={styles.title}>
              <span className={styles.icon}>📚</span>
              Understanding Your Mortgage
            </CardTitle>
          </CardHeader>
          <CardContent className={styles.content}>
            <Accordion type="single" collapsible className={styles.accordion}>
              <AccordionItem value="what-is-mortgage">
                <AccordionTrigger value="what-is-mortgage">What is a mortgage?</AccordionTrigger>
                <AccordionContent value="what-is-mortgage">
                  A mortgage is a loan used to purchase real estate. The property serves as collateral,
                  and you make regular payments over a set period (amortization) until the loan is fully
                  repaid. In Canada, mortgages typically have terms of 1-5 years with amortization periods
                  up to 30 years.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="fixed-vs-variable">
                <AccordionTrigger value="fixed-vs-variable">Fixed vs. Variable Rates</AccordionTrigger>
                <AccordionContent value="fixed-vs-variable">
                  <strong>Fixed Rate:</strong> Your interest rate stays the same for the entire term,
                  providing payment stability and protection from rate increases.
                  <br /><br />
                  <strong>Variable Rate:</strong> Your rate fluctuates with the prime rate. Generally
                  starts lower than fixed rates but can increase or decrease over time.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="amortization">
                <AccordionTrigger value="amortization">What is amortization?</AccordionTrigger>
                <AccordionContent value="amortization">
                  Amortization is the total length of time it will take to pay off your mortgage completely.
                  In Canada, the maximum amortization period is typically 25 years with less than 20% down,
                  or up to 30 years with 20% or more down payment. A shorter amortization means higher
                  monthly payments but less interest paid overall.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardHeader className={styles.header}>
            <CardTitle className={styles.title}>
              <span className={styles.icon}>🛡️</span>
              CMHC Insurance
            </CardTitle>
          </CardHeader>
          <CardContent className={styles.content}>
            <Accordion type="single" collapsible className={styles.accordion}>
              <AccordionItem value="what-is-cmhc">
                <AccordionTrigger value="what-is-cmhc">What is CMHC insurance?</AccordionTrigger>
                <AccordionContent value="what-is-cmhc">
                  CMHC (Canada Mortgage and Housing Corporation) insurance is required when your down
                  payment is less than 20% of the home&apos;s purchase price. This insurance protects the
                  lender if you default on your mortgage. The premium is typically added to your mortgage
                  amount and ranges from 0.6% to 4% of your loan.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="avoid-cmhc">
                <AccordionTrigger value="avoid-cmhc">How to avoid CMHC fees?</AccordionTrigger>
                <AccordionContent value="avoid-cmhc">
                  The only way to avoid CMHC insurance fees is to make a down payment of 20% or more
                  of the home&apos;s purchase price. This can save you thousands of dollars in insurance
                  premiums. Even increasing your down payment from 15% to 20% can result in significant
                  savings.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardHeader className={styles.header}>
            <CardTitle className={styles.title}>
              <span className={styles.icon}>💰</span>
              Down Payments
            </CardTitle>
          </CardHeader>
          <CardContent className={styles.content}>
            <Accordion type="single" collapsible className={styles.accordion}>
              <AccordionItem value="minimum-down">
                <AccordionTrigger value="minimum-down">Minimum down payment requirements</AccordionTrigger>
                <AccordionContent value="minimum-down">
                  <ul className={styles.list}>
                    <li className={styles.listItem}>• <strong>Under $500,000:</strong> 5% minimum</li>
                    <li className={styles.listItem}>• <strong>$500,000 - $999,999:</strong> 5% on first $500k, 10% on remainder</li>
                    <li className={styles.listItem}>• <strong>$1,000,000+:</strong> 20% minimum (no CMHC insurance available)</li>
                  </ul>
                  <p style={{ marginTop: '0.75rem' }}>
                    First-time homebuyers may qualify for programs like the Home Buyers&apos; Plan or
                    First-Time Home Buyer Incentive.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="down-payment-sources">
                <AccordionTrigger value="down-payment-sources">Where can down payment funds come from?</AccordionTrigger>
                <AccordionContent value="down-payment-sources">
                  Acceptable sources include: personal savings, RRSP withdrawals (Home Buyers&apos; Plan),
                  gifted funds from family, proceeds from selling another property, or borrowed funds
                  from a secured line of credit. Some sources may require documentation and affect
                  your mortgage approval.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardHeader className={styles.header}>
            <CardTitle className={styles.title}>
              <span className={styles.icon}>📈</span>
              Saving Strategies
            </CardTitle>
          </CardHeader>
          <CardContent className={styles.content}>
            <Accordion type="single" collapsible className={styles.accordion}>
              <AccordionItem value="payment-frequency">
                <AccordionTrigger value="payment-frequency">Payment frequency benefits</AccordionTrigger>
                <AccordionContent value="payment-frequency">
                  <strong>Accelerated Bi-weekly:</strong> Make 26 payments per year (equivalent to
                  13 monthly payments instead of 12). This can reduce your amortization by 3-5 years
                  and save tens of thousands in interest.
                  <br /><br />
                  <strong>Weekly:</strong> Similar benefits to bi-weekly but with smaller, more
                  frequent payments that may align better with your income schedule.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="prepayment">
                <AccordionTrigger value="prepayment">Prepayment privileges</AccordionTrigger>
                <AccordionContent value="prepayment">
                  Most mortgages allow you to prepay a certain percentage (usually 10-20%) of your
                  original principal each year without penalty. Even small prepayments can significantly
                  reduce your total interest and shorten your amortization period. Consider making
                  lump-sum payments with tax refunds, bonuses, or extra income.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="rate-shopping">
                <AccordionTrigger value="rate-shopping">Shopping for the best rate</AccordionTrigger>
                <AccordionContent value="rate-shopping">
                  Even a 0.25% difference in interest rate can save you thousands over your mortgage
                  term. Work with a mortgage broker to compare rates from multiple lenders, consider
                  both banks and credit unions, and don&apos;t be afraid to negotiate. Remember to compare
                  not just rates, but also terms, prepayment privileges, and penalties.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>

      <Card className={buildClassName(cardStyles.shadowSmooth, styles.cardHighlighted)}>
        <CardContent className={styles.proTipContent}>
          <div className={styles.proTipHeader}>
            <span className={styles.proTipIcon}>🧮</span>
            <div>
              <h4 className={styles.proTipTitle}>Pro Tip: The Stress Test</h4>
              <p className={styles.proTipText}>
                Canadian lenders must qualify you at a rate higher than your actual mortgage rate
                (currently 5.25% or your rate + 2%, whichever is higher). This &quot;stress test&quot; ensures
                you can still afford your mortgage if rates increase. Plan your budget accordingly
                and consider what you&apos;d do if rates rise significantly at renewal time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EducationalContent;
